import React, { useEffect, useMemo, useState } from 'react'
import { generatePath } from 'react-router-dom'
import { DataCard, DataColumn, DataGrid } from '@/components/DataCard/DataCard'
import {
  FormField,
  FormReadonlyField,
  FormSelectField,
  FormTextareaField,
} from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import { ClientDto } from 'api/Clients/Types/Client'
import { TradingAccountDto } from 'api/Clients/Types/TradingAccount'
import { NameDto } from 'api/Common/Types'
import { PermissionDto } from 'api/Identity/Types/PermissionDto'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { DividendPaymentDataDto } from 'api/Trading-Accounts/Types/DividendsDto'
import { InstrumentDto } from 'api/Trading-Accounts/Types/InstrumentDto'
import { isCQGCastPlatform } from 'api/Trading-Accounts/Types/PlatformTypeDto'
import { WalletDirectionTypeId } from 'api/Transaction/Types/Transaction'
import { TransactionDirectionType } from 'api/Transaction/Types/TransactionDirectionType'
import { TransactionTypeDto } from 'api/Wallet/Types/TransactionTypeDto'
import { CurrencyType, DragonPhoenixCurrencyType, PHPCurrencyType } from 'api/Wallet/Types/Wallet'
import { Link } from 'components/Link'
import { format } from 'date-fns'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import toNumber from 'lodash/toNumber'
import { TransactionDetailsView } from 'routes/Transactions/Details/ResolveTransactionDetailsRoute'
import { useApiClient } from 'utils/api/ApiClient'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { links } from 'utils/featureToggles'
import { FormSubmitValues } from 'utils/formValidation'
import { hasPermission } from 'utils/permissionsValidator'

export interface AdjustmentTradingAccountFormValues {
  client: string | null
  tradingAccountId: string
  transactionTypeId: number
  directionTypeId: string
  amount: number
  comment: string
  withholdingTax?: number
  grossAmount?: number
  instrumentId?: string
  dividendPaymentDataId?: string
  currency: CurrencyType
}

export interface AdjustmentTradingAccountSubmitValues extends AdjustmentTradingAccountFormValues {
  TradingAccountCurrencyId: CurrencyType
}

function dataToSubmit(
  values: AdjustmentTradingAccountFormValues
): FormSubmitValues<AdjustmentTradingAccountSubmitValues> {
  if (values.transactionTypeId === TransactionDetailsView.DividendAdjustment) {
    return {
      client: values.client,
      tradingAccountId: values.tradingAccountId,
      grossAmount: Number(values.amount),
      amount: Number(values.grossAmount),
      comment: values.comment || '',
      directionTypeId: values.directionTypeId.toString(),
      transactionTypeId: values.transactionTypeId,
      instrumentId: values.instrumentId,
      dividendPaymentDataId: values.dividendPaymentDataId,
      currency: values.currency,
      TradingAccountCurrencyId: values.currency,
    }
  }

  return {
    client: values.client,
    tradingAccountId: values.tradingAccountId,
    amount: Number(values.amount),
    comment: values.comment || '',
    directionTypeId: values.directionTypeId.toString(),
    transactionTypeId: values.transactionTypeId,
    currency: values.currency,
    TradingAccountCurrencyId: values.currency,
  }
}

const AdjustmentTradingAccountFormUI: React.FC<
  FormikProps<AdjustmentTradingAccountFormValues> & OuterProps
> = (props) => {
  const {
    client,
    permissions,
    handleSubmit,
    isSubmitting,
    values,
    errors,
    setFieldValue,
    onSymbolChange,
    dateFormatType,
  } = props

  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)
  const [displayCurrencies, setDisplayCurrencies] = useState<NameDto<string>[]>([])

  useEffect(() => {
    props.resetForm()
  }, [client?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const getDisplayCurrencies = async () => {
      const response = await tradingAccountsApiClient.getDisplayCurrencies()
      setDisplayCurrencies(response as NameDto<string>[])
    }

    getDisplayCurrencies()
  }, [tradingAccountsApiClient])

  const currentTradingAccount = useMemo(
    () => props.tradingAccounts.find((item) => item.id === values.tradingAccountId),
    [props.tradingAccounts, values.tradingAccountId]
  )
  const transactionTypeOptions = useMemo(
    () =>
      props.transactionTypes.map((transactionType) => ({
        id: transactionType.id,
        name: transactionType.name ?? '',
      })),
    [props.transactionTypes]
  )

  const selectedTransactionType = useMemo(
    () => props.transactionTypes.find((item) => item.id === Number(values.transactionTypeId)),
    [props.transactionTypes, values.transactionTypeId]
  )

  const isDividendAdjustment =
    Number(values.transactionTypeId) === TransactionDetailsView.DividendAdjustment
  const isCQGCurrencySelection =
    currentTradingAccount && isCQGCastPlatform(currentTradingAccount.platformType.id)
  const isDirectionLocked = Boolean(selectedTransactionType?.directionType)

  const calculateGrossAmount = (
    amount: number,
    tax: number,
    directionTypeId: number | string
  ): number => {
    if (Number(directionTypeId) === WalletDirectionTypeId.DEBIT) {
      return amount
    }

    return Math.round(amount * (1 - tax) * 100) / 100
  }

  const handleSymbol = (value: string | number) => {
    const nextInstrument = props.instruments.find((instrument) => instrument.id === value)

    if (!nextInstrument) {
      setFieldValue('instrumentId', undefined)
      setFieldValue('withholdingTax', undefined)
      setFieldValue('dividendPaymentDataId', undefined)
      return
    }

    setFieldValue('instrumentId', nextInstrument.id)
    setFieldValue('withholdingTax', nextInstrument.withholdingTax)
    setFieldValue('dividendPaymentDataId', undefined)

    if (values.amount >= 0) {
      setFieldValue(
        'grossAmount',
        calculateGrossAmount(
          Number(values.amount),
          nextInstrument.withholdingTax,
          values.directionTypeId
        )
      )
    }

    onSymbolChange(nextInstrument)
  }

  return (
    <Form onSubmit={handleSubmit}>
      <DataGrid
        columns={1}
        rightAddon={
          <ButtonGroup>
            {hasPermission(permissions, PermissionsEnum.CanUploadCsvFilesToCreateTransactions) && (
              <Button onClick={props.showImportTransactionModal} variant='outline' type='button'>
                Upload
              </Button>
            )}
          </ButtonGroup>
        }
      >
        <DataColumn>
          <DataCard title='Adjustment' columns={2}>
            <FormReadonlyField
              name='client'
              label='Client'
              value={values.client ?? ''}
              rightAddon={
                <>
                  <Button type='button' size='sm' variant='outline' onClick={props.setClient}>
                    Select
                  </Button>
                  {client?.id && (
                    <Link
                      to={generatePath(links.recordsClients.detailsUrl as string, {
                        id: client.id,
                        slug: 'details',
                      })}
                      showIcon
                    />
                  )}
                </>
              }
            />
            <FormSelectField
              name='tradingAccountId'
              label='Trading Account'
              value={values.tradingAccountId || undefined}
              options={props.tradingAccounts.map((account) => ({
                id: account.id,
                name: `${account.name}-${account.accountGroup.name}-balance: ${account.platformOverview.balance} ${account.currency.id}`,
              }))}
              error={errors.tradingAccountId}
              showSearch
              onChange={(value) => {
                const nextTradingAccountId = value ? String(value) : ''
                props.setFieldValue('tradingAccountId', nextTradingAccountId)
                props.setTradingAccount(nextTradingAccountId)
              }}
              rightAddon={
                values.tradingAccountId && (
                  <Link
                    to={generatePath(links.recordsTradingAccounts.detailsUrl as string, {
                      id: values.tradingAccountId,
                      slug: 'details',
                    })}
                    showIcon
                  />
                )
              }
            />
            <FormSelectField
              name='transactionTypeId'
              label='Transaction Type'
              value={values.transactionTypeId || undefined}
              options={transactionTypeOptions}
              error={errors.transactionTypeId}
              onChange={(value) => {
                const nextTransactionType = props.transactionTypes.find(
                  (item) => item.id === Number(value)
                )

                if (!nextTransactionType) {
                  return
                }

                setFieldValue('transactionTypeId', nextTransactionType.id)

                if (nextTransactionType.directionType) {
                  setFieldValue('directionTypeId', nextTransactionType.directionType.id.toString())
                }
              }}
            />
            <FormSelectField
              name='directionTypeId'
              label='Direction'
              value={values.directionTypeId || undefined}
              options={props.directionTypes.map((directionType) => ({
                id: directionType.id.toString(),
                name: directionType.name,
              }))}
              error={errors.directionTypeId}
              disabled={isDirectionLocked}
              onChange={(value) => {
                setFieldValue('directionTypeId', String(value))
                setFieldValue(
                  'grossAmount',
                  calculateGrossAmount(
                    Number(values.amount),
                    values.withholdingTax || 0,
                    String(value)
                  )
                )
              }}
            />
            <FormField
              name='amount'
              label='Amount'
              type='number'
              error={errors.amount}
              onChange={(event) => {
                setFieldValue('amount', event.target.value)
                setFieldValue(
                  'grossAmount',
                  calculateGrossAmount(
                    Number(event.target.value),
                    values.withholdingTax || 0,
                    values.directionTypeId
                  )
                )
              }}
              rightAddon={
                !isCQGCurrencySelection && props.currency ? (
                  <span className='inline-flex items-center px-3 text-sm text-muted-foreground'>
                    {props.currency}
                  </span>
                ) : undefined
              }
            />
            {isCQGCurrencySelection && (
              <FormSelectField
                name='currency'
                label='Display Currency'
                value={values.currency}
                options={displayCurrencies}
                error={errors.currency}
                onChange={(value) => setFieldValue('currency', String(value))}
              />
            )}
            <FormTextareaField name='comment' label='Comment' rows={3} maxLength={130} />
          </DataCard>

          {isDividendAdjustment && (
            <DataCard title='Dividend Details' columns={2}>
              <FormSelectField
                name='instrumentId'
                label='Symbol'
                value={values.instrumentId || undefined}
                options={props.instruments.map((instrument) => ({
                  id: instrument.id,
                  name: instrument.symbol,
                }))}
                error={errors.instrumentId}
                showSearch
                allowClear
                onChange={handleSymbol}
              />
              {values.instrumentId && (
                <FormSelectField
                  name='dividendPaymentDataId'
                  label='Dividend Data'
                  value={values.dividendPaymentDataId || undefined}
                  options={props.dividends.map((dividend) => ({
                    id: dividend.id,
                    name: getDividendDataName(dividend, dateFormatType),
                  }))}
                  error={errors.dividendPaymentDataId}
                  showSearch
                  allowClear
                  onChange={(value) => setFieldValue('dividendPaymentDataId', String(value))}
                />
              )}
              <FormReadonlyField
                label='WithHolding TAX'
                value={values.withholdingTax ? `${values.withholdingTax * 100}%` : '0%'}
              />
              <FormReadonlyField
                label='Net Amount'
                value={values.grossAmount?.toFixed(2) ?? ''}
                rightAddon={
                  values.currency ? (
                    <span className='inline-flex items-center px-3 text-sm text-muted-foreground'>
                      {values.currency}
                    </span>
                  ) : undefined
                }
              />
            </DataCard>
          )}
        </DataColumn>
      </DataGrid>
      <div className='mt-4 flex justify-center'>
        <Button disabled={isSubmitting} type='submit'>
          Submit
        </Button>
      </div>
    </Form>
  )
}

interface OuterProps {
  client: ClientDto | null
  tradingAccounts: TradingAccountDto[]
  permissions: PermissionDto[]
  currency: null | CurrencyType | DragonPhoenixCurrencyType | PHPCurrencyType | string
  tradingAccountId: string
  directionTypes: TransactionDirectionType[]
  transactionTypes: TransactionTypeDto[]
  instruments: InstrumentDto[]
  dividends: DividendPaymentDataDto[]
  dateFormatType: string

  onSubmit(values: FormSubmitValues<AdjustmentTradingAccountFormValues>): Promise<void>
  onSymbolChange(instruments: InstrumentDto): void
  showImportTransactionModal(): void
  setClient(): void
  setCurrency(currency: CurrencyType | DragonPhoenixCurrencyType | PHPCurrencyType | string): void
  setTradingAccount(tradingAccountId: string): void
}

export const AdjustmentTradingAccountForm = withFormik<
  OuterProps,
  AdjustmentTradingAccountFormValues
>({
  mapPropsToValues: ({ client, tradingAccountId, currency }) => ({
    client: client?.name || null,
    tradingAccountId,
    amount: 0,
    comment: '',
    directionTypeId: '100',
    transactionTypeId: 0,
    withholdingTax: undefined,
    currency: currency as CurrencyType,
  }),
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      props.setCurrency(values.currency)
      await props.onSubmit(dataToSubmit(values))
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<AdjustmentTradingAccountFormValues> = {}

    if (!values.currency) {
      errors.currency = 'Required'
    }

    if (!values.directionTypeId) {
      errors.directionTypeId = 'Required'
    }

    if (!values.transactionTypeId) {
      errors.transactionTypeId = 'Required'
    }

    if (!values.tradingAccountId) {
      errors.tradingAccountId = 'Required'
    }

    if (
      values.transactionTypeId === TransactionDetailsView.DividendAdjustment &&
      !values.instrumentId
    ) {
      errors.instrumentId = 'Required'
    }

    if (
      Number(values.transactionTypeId) === TransactionDetailsView.DividendAdjustment &&
      values.instrumentId &&
      !values.dividendPaymentDataId
    ) {
      errors.dividendPaymentDataId = 'Required'
    }

    if (!values.amount) {
      errors.amount = 'Required'
    } else if (toNumber(values.amount) <= 0) {
      errors.amount = 'Must be greater than 0'
    }

    return errors
  },
  enableReinitialize: true,
})(AdjustmentTradingAccountFormUI)

const getDividendDataName = (
  dividendPaymentData: DividendPaymentDataDto,
  dateFormatType: string
) => {
  return `${dividendPaymentData.eventSubType.name}, ExDate=${format(
    new Date(dividendPaymentData.eventDate),
    dateFormatType
  )}, Rate=${dividendPaymentData.amount} ${dividendPaymentData.currency.id}`
}
